// assets/js/organisers.js
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

const isEditor = document.body.classList.contains("wp-admin");
if (!isEditor) gsap.registerPlugin(ScrollTrigger);

function openPanel(item) {
  const btn = item.querySelector(".organisers__trigger");
  const panel = item.querySelector(".organisers__panel");
  if (!btn || !panel) return;

  gsap.killTweensOf(panel);

  item.classList.add("is-open");
  btn.setAttribute("aria-expanded", "true");

  panel.hidden = false;
  panel.style.overflow = "hidden";

  const h = panel.scrollHeight;

  gsap.fromTo(
    panel,
    { height: 0, opacity: 0 },
    {
      height: h,
      opacity: 1,
      duration: 0.35,
      ease: "power2.out",
      onComplete: () => {
        panel.style.height = "";
        panel.style.overflow = "";
      },
    },
  );
}

function closePanel(item, immediate = false) {
  const btn = item.querySelector(".organisers__trigger");
  const panel = item.querySelector(".organisers__panel");
  if (!btn || !panel) return;

  gsap.killTweensOf(panel);

  item.classList.remove("is-open");
  btn.setAttribute("aria-expanded", "false");

  if (immediate) {
    panel.hidden = true;
    panel.style.height = "";
    panel.style.overflow = "";
    panel.style.opacity = "";
    return;
  }

  panel.style.overflow = "hidden";

  gsap.to(panel, {
    height: 0,
    opacity: 0,
    duration: 0.28,
    ease: "power2.inOut",
    onComplete: () => {
      panel.hidden = true;
      panel.style.height = "";
      panel.style.overflow = "";
      panel.style.opacity = "";
    },
  });
}

function initOrganisers(section) {
  if (!section || section.__orgInit) return;
  section.__orgInit = true;

  const items = Array.from(section.querySelectorAll(".organisers__item"));

  // Ensure correct initial state (only first open if multiple were set)
  const openItems = items.filter((it) => it.classList.contains("is-open"));
  if (openItems.length > 1) {
    openItems.slice(1).forEach((it) => closePanel(it, true));
  }
  items.forEach((it) => {
    const panel = it.querySelector(".organisers__panel");
    if (!panel) return;
    panel.hidden = !it.classList.contains("is-open");
  });

  items.forEach((item) => {
    const btn = item.querySelector(".organisers__trigger");
    const panel = item.querySelector(".organisers__panel");
    if (!btn || !panel) return;

    btn.addEventListener("click", () => {
      const isOpen = item.classList.contains("is-open");

      // Close all others
      items.forEach((other) => {
        if (other !== item && other.classList.contains("is-open")) {
          closePanel(other);
        }
      });

      // Toggle clicked
      if (isOpen) closePanel(item);
      else openPanel(item);
    });
  });

  // Optional section reveal animation (same as before)
  const eyebrow = section.querySelector(".organisers__eyebrow");
  const headline = section.querySelector(".organisers__headline");
  const intro = section.querySelector(".organisers__intro");
  const list = section.querySelector(".organisers__list");

  gsap.set([eyebrow, headline, intro], { opacity: 0, y: 18 });
  if (list) gsap.set(list.children, { opacity: 0, y: 14 });

  const tlOpts = isEditor
    ? { defaults: { ease: "power3.out" } }
    : {
        defaults: { ease: "power3.out" },
        scrollTrigger: { trigger: section, start: "top 78%", once: true },
      };

  const tl = gsap.timeline(tlOpts);
  if (eyebrow) tl.to(eyebrow, { opacity: 1, y: 0, duration: 0.45 });
  if (headline) tl.to(headline, { opacity: 1, y: 0, duration: 0.7 }, "-=0.2");
  if (intro) tl.to(intro, { opacity: 1, y: 0, duration: 0.55 }, "-=0.35");
  if (list)
    tl.to(
      list.children,
      { opacity: 1, y: 0, duration: 0.45, stagger: 0.08 },
      "-=0.25",
    );
}

const mount = () => {
  if (!isEditor) {
    document.querySelectorAll(".organisers").forEach(initOrganisers);
    return;
  }

  const root = document.querySelector(".editor-styles-wrapper") || document;
  const run = () =>
    root.querySelectorAll(".organisers").forEach(initOrganisers);

  run();
  const mo = new MutationObserver(run);
  mo.observe(root, { childList: true, subtree: true });
};

if (window.wp?.domReady) window.wp.domReady(mount);
else document.addEventListener("DOMContentLoaded", mount);
